import axios, { type AxiosResponse, type AxiosError } from 'axios';
import { getItem } from '@/utils/localStorageHelpers';
import { enqueueSnackbar } from 'notistack';
import appConfig from '@/utils/appConfig';

const api = axios.create({
  baseURL: appConfig.API_URL,
  timeout: Number(appConfig.APP_TIMEOUT) || 30000,
  withCredentials: true,
});

api.interceptors.request.use(
  (config) => {
    // append language to header
    const language = getItem('language');
    config.headers.Language = language || '1';

    // append token to header
    const token = getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

api.interceptors.response.use(
  (response: AxiosResponse) => response,
  (error: AxiosError) => {
    // Handle network errors (e.g., connection refused, no internet)
    if (error.code === 'ERR_NETWORK' || !error.response) {
      enqueueSnackbar(
        'Connection error. Please check your internet connection or try again later.',
        { variant: 'error' },
      );
      return Promise.reject(error);
    }

    // Handle timeout errors
    if (error.code === 'ECONNABORTED' || error.message.includes('timeout')) {
      enqueueSnackbar('Request timed out. Please try again.', { variant: 'error' });
      return Promise.reject(error); // Reject the promise so calling code can also handle it
    }

    // on API Conflict response, display message returned
    if (error.response && error.response.status === 409) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const msg = (error.response.data as any)?.message;
      enqueueSnackbar(msg, { variant: 'error' });
    }

    return Promise.reject(error);
  },
);

export default api;
